import React, { useState, useEffect } from "react";
import AttendanceAnalysis from "./AttendanceAnalysis";
import PendingLeaveApprovals from "./PendingLeaveApprovals";
import no_user_image from ".././images/no_user_image.svg";

// --- Mobile-Responsive Leave Approvals Component ---
// --- Mobile-Responsive Leave Approvals Component (With Pagination & Height Limits) ---
function LeaveApprovalsTable({ baseUrl, companyCode }) {
  const [leaveData, setLeaveData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- Pagination & View Configuration States ---
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  useEffect(() => {
    const fetchLeaveApprovals = async () => {
      setIsLoading(true);
      setError(null);
      try {
        let url = `${baseUrl}/api/PersonalInfoLeaveApplicationList/${companyCode}/APPROVED/-`;
        const response = await fetch(url);

        if (!response.ok) throw new Error("Failed to load leave logs");
        const data = await response.json();
        setLeaveData(Array.isArray(data) ? data : []);
        setCurrentPage(1); // Reset page on new data fetch
      } catch (err) {
        console.error("Error fetching leave approvals:", err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    if (baseUrl && companyCode) {
      fetchLeaveApprovals();
    }
  }, [baseUrl, companyCode]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return dateStr.split("T")[0];
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-gray-400 gap-2 text-xs">
        <div className="w-5 h-5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
        <span>Syncing approved leave logs...</span>
      </div>
    );
  }

  if (error) {
    return (
      <p className="text-center text-xs text-rose-500 py-6 font-medium">
        Error loading history: {error}
      </p>
    );
  }

  if (leaveData.length === 0) {
    return (
      <p className="text-center text-xs text-gray-400 py-6">
        No past approved leave records found.
      </p>
    );
  }

  // --- Front-end Pagination Math ---
  const totalItems = leaveData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentLeaveItems = leaveData.slice(startIndex, endIndex);

  return (
    <div className="space-y-3">
      {/* 1. Desktop & Tablet View Table layout (Constrained Height Scrollbox) */}
      <div className="hidden md:block overflow-x-auto border border-gray-100 rounded-lg max-h-[360px] overflow-y-auto shadow-inner bg-gray-50/30">
        <table className="w-full text-left border-collapse text-xs relative">
          <thead className="sticky top-0 bg-gray-50 shadow-sm z-10">
            <tr className="border-b border-gray-100 text-gray-600 font-semibold">
              <th className="p-3">Employee Details</th>
              <th className="p-3">Category</th>
              <th className="p-3">Duration (Days)</th>
              <th className="p-3">Status</th>
              <th className="p-3">Approved By</th>
              <th className="p-3">Reason</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white text-gray-700">
            {currentLeaveItems.map((item, index) => (
              <tr
                key={item.serialcode || index}
                className="hover:bg-gray-50/60 transition-colors"
              >
                <td className="p-3">
                  <div className="font-semibold text-gray-900">
                    {item.Name?.trim()}
                  </div>
                  <div className="text-[10px] text-gray-400 font-mono mt-0.5">
                    ID: {item.EmpId} | {item.JobTitle || "Staff"}
                  </div>
                </td>
                <td className="p-3">
                  <span className="bg-amber-50 text-amber-700 border border-amber-100/70 px-1.5 py-0.5 rounded font-medium text-[11px]">
                    {item.Leave_Category}
                  </span>
                </td>
                <td className="p-3 font-medium">
                  <div>
                    {formatDate(item.FromDate)} to {formatDate(item.ToDate)}
                  </div>
                  <div className="text-[10px] text-gray-400 mt-0.5">
                    {item.Days} {item.Days === 1 ? "Day" : "Days"}
                  </div>
                </td>
                <td className="p-3">
                  <span className="bg-emerald-50 text-emerald-700 border border-emerald-100 px-2 py-0.5 rounded-full font-bold text-[10px] uppercase tracking-wider">
                    {item.ApprovalStatus}
                  </span>
                </td>
                <td className="p-3 text-gray-500">
                  <div className="font-medium">{item.ApprovedBy || "—"}</div>
                  <div className="text-[10px] text-gray-400 mt-0.5">
                    {formatDate(item.ApprovalDate)}
                  </div>
                </td>
                <td
                  className="p-3 max-w-xs truncate text-gray-500 italic"
                  title={item.Reason}
                >
                  {item.Reason || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 2. Phone Layout (Cards deck - Constrained Height Scrollbox) */}
      <div className="grid grid-cols-1 gap-3 md:hidden max-h-[400px] overflow-y-auto p-1 border border-transparent rounded-lg">
        {currentLeaveItems.map((item, index) => (
          <div
            key={item.serialcode || index}
            className="bg-white border border-gray-100 rounded-lg p-3 text-xs space-y-2 shadow-xs"
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <h4 className="font-bold text-gray-900">{item.Name?.trim()}</h4>
                <p className="text-[10px] text-gray-400 font-mono mt-0.5">
                  ID: {item.EmpId} • {item.JobTitle || "Staff"}
                </p>
              </div>
              <span className="bg-emerald-50 text-emerald-700 border border-emerald-100 px-2 py-0.5 rounded-full font-bold text-[9px] uppercase tracking-wide shrink-0">
                {item.ApprovalStatus}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-2 bg-gray-50/80 p-2 rounded border border-gray-100/50 text-[11px]">
              <div>
                <span className="block text-[10px] text-gray-400 font-medium">
                  Category:
                </span>
                <span className="font-semibold text-amber-800">
                  {item.Leave_Category}
                </span>
              </div>
              <div>
                <span className="block text-[10px] text-gray-400 font-medium">
                  Duration:
                </span>
                <span className="font-semibold text-gray-700">
                  {item.Days} {item.Days === 1 ? "Day" : "Days"}
                </span>
              </div>
              <div className="col-span-2 pt-1 border-t border-gray-200/40">
                <span className="block text-[10px] text-gray-400 font-medium">
                  Date Range:
                </span>
                <span className="font-mono text-gray-600">
                  {formatDate(item.FromDate)} → {formatDate(item.ToDate)}
                </span>
              </div>
            </div>

            <div className="text-[11px] space-y-0.5 text-gray-600">
              <p>
                <span className="text-gray-400 font-medium">Approved By:</span>{" "}
                {item.ApprovedBy}{" "}
                <span className="text-gray-400 text-[10px]">
                  ({formatDate(item.ApprovalDate)})
                </span>
              </p>
              {item.Reason && (
                <p className="pt-1 text-gray-400 italic bg-white border-l-2 border-gray-200 pl-1.5 line-clamp-2 mt-1">
                  "{item.Reason}"
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* 3. Pagination Controls Strip */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2 text-xs text-gray-500 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <span>Show</span>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="border border-gray-200 rounded pl-2 pr-7 py-1 text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-amber-500 font-medium cursor-pointer appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%236b7280%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E')] bg-[length:10px_auto] bg-[right_0.5rem_center] bg-no-repeat"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
          </select>
          <span className="text-gray-400">
            (Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of{" "}
            {totalItems})
          </span>
        </div>

        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-2.5 py-1 rounded border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-white transition-colors select-none font-medium cursor-pointer"
          >
            Prev
          </button>

          <span className="px-2 font-medium text-gray-700">
            Page {currentPage} of {totalPages || 1}
          </span>

          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages || totalPages === 0}
            className="px-2.5 py-1 rounded border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-white transition-colors select-none font-medium cursor-pointer"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

// end of leave approvals table

// --- Primary HR Dashboard Export ---
export default function HrDashboard({
  baseUrl = "https://cubixweberp.com:213",
  company_code_from_local_storage = "cubix",
  // The Employee ID of whoever is logged in — used to fetch "my team's pending
  // leave requests". Falls back to localStorage("currentUserEmployeeID") if not
  // passed explicitly; update the key below if your app stores it elsewhere.
  loggedEmpId = localStorage.getItem("currentUserEmployeeID") || "",
}) {
  // ─── SELF-FETCHED ATTENDANCE SUMMARY ───
  // HrDashboard is only ever mounted when the HR tab is active, so fetching
  // here (rather than being fed data from a parent) naturally means these
  // calls only fire when the HR tab is actually opened.
  const [presentData, setPresentData] = useState([]);
  const [absentData, setAbsentData] = useState([]);
  const [leaveData, setLeaveData] = useState([]);
  const [isLoadingAttendanceSummary, setIsLoadingAttendanceSummary] =
    useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchAttendanceSummary = async () => {
      setIsLoadingAttendanceSummary(true);
      try {
        const urlPresentEmployee = `${baseUrl}/api/CpaysCount/${company_code_from_local_storage}/ATTENDANCEDAILY/-/-`;
        const urlAbsentEmployee = `${baseUrl}/api/CpaysCount/${company_code_from_local_storage}/ATTENDANCEDAILY_ABS/-/-`;
        const urlLeaveEmployee = `${baseUrl}/api/CpaysCount/${company_code_from_local_storage}/ATTENDANCEDAILY_LEAVE/-/-`;

        const [presentRes, absentRes, leaveRes] = await Promise.all([
          fetch(urlPresentEmployee),
          fetch(urlAbsentEmployee),
          fetch(urlLeaveEmployee),
        ]);

        const [presentJson, absentJson, leaveJson] = await Promise.all([
          presentRes.ok ? presentRes.json() : [],
          absentRes.ok ? absentRes.json() : [],
          leaveRes.ok ? leaveRes.json() : [],
        ]);

        if (!isMounted) return;
        setPresentData(Array.isArray(presentJson) ? presentJson : []);
        setAbsentData(Array.isArray(absentJson) ? absentJson : []);
        setLeaveData(Array.isArray(leaveJson) ? leaveJson : []);
      } catch (err) {
        console.error("Error fetching HR attendance summary:", err);
      } finally {
        if (isMounted) setIsLoadingAttendanceSummary(false);
      }
    };

    fetchAttendanceSummary();

    return () => {
      isMounted = false;
    };
  }, [baseUrl, company_code_from_local_storage]);


  let apiPaths_from_local_storage = localStorage.getItem("apiPaths")
    ? JSON.parse(localStorage.getItem("apiPaths"))
    : "-";

  const [activeModal, setActiveModal] = useState(null);

  // States for expandable punch logs (Attendance)
  const [expandedEmpId, setExpandedEmpId] = useState(null);
  const [punchData, setPunchData] = useState([]);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  // --- Document Expiry States ---
  const [activeDocModal, setActiveDocModal] = useState(null);
  const [docDetailsData, setDocDetailsData] = useState([]);
  const [isLoadingDocDetails, setIsLoadingDocDetails] = useState(false);

  const [expiryMetrics, setExpiryMetrics] = useState({
    passport: {
      title: "Passport",
      count: 0,
      color: "text-blue-700 bg-blue-50/50 border-blue-100 hover:bg-blue-50",
      listEndpoint: "PASSPORTEXPLIST",
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
    visa: {
      title: "Visa",
      count: 0,
      color:
        "text-purple-700 bg-purple-50/50 border-purple-100 hover:bg-purple-50",
      listEndpoint: "VISAEXPLIST",
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      ),
    },
    nationalid: {
      title: "National ID",
      count: 0,
      color:
        "text-indigo-700 bg-indigo-50/50 border-indigo-100 hover:bg-indigo-50",
      listEndpoint: "NATIONALIDEXPLIST",
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2"
          />
        </svg>
      ),
    },
    personid: {
      title: "Person ID",
      count: 0,
      color: "text-teal-700 bg-teal-50/50 border-teal-100 hover:bg-teal-50",
      listEndpoint: "PERSONIDEXPLIST",
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
      ),
    },
    drivinglic: {
      title: "Driving License",
      count: 0,
      color: "text-cyan-700 bg-cyan-50/50 border-cyan-100 hover:bg-cyan-50",
      listEndpoint: "DRIVINGLICEXPLIST",
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
          />
        </svg>
      ),
    },
    insurance: {
      title: "Insurance",
      count: 0,
      color: "text-sky-700 bg-sky-50/50 border-sky-100 hover:bg-sky-50",
      listEndpoint: "INSURANCEEXPLIST",
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
          />
        </svg>
      ),
    },
    fleet: {
      title: "Fleet Vehicle",
      count: 0,
      color: "text-slate-700 bg-slate-50/50 border-slate-100 hover:bg-slate-50",
      listEndpoint: "FLEETLIST",
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 011-1v-2.5a1 1 0 01.293-.707l2.828-2.828A1 1 0 0117.828 8H21v8a1 1 0 01-1 1h-1M13 12h4"
          />
        </svg>
      ),
    },
    FLEETINSURANCECNT: {
      title: "Fleet Insurance",
      count: 0,
      color:
        "text-orange-700 bg-orange-50/50 border-orange-100 hover:bg-orange-50",
      listEndpoint: "FLEETINSURANCE",
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z"
          />
        </svg>
      ),
    },
    PARTTIMECNT: {
      title: "Part-Time Count",
      count: 0,
      color: "text-pink-700 bg-pink-50/50 border-pink-100 hover:bg-pink-50",
      listEndpoint: "PARTTIMELIST",
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
    COMPANYDOCSEXPLISTCNT: {
      title: "Company Doc",
      count: 0,
      color: "text-rose-700 bg-rose-50/50 border-rose-100 hover:bg-rose-50",
      listEndpoint: "COMPANYDOCSEXPLIST",
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      ),
    },
  });
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(true);

  // Fetch Expiry Dashboard Summary Metrics
  useEffect(() => {
    const fetchExpiryData = async () => {
      setIsLoadingMetrics(true);
      const updatedMetrics = { ...expiryMetrics };
      const endpointKeys = Object.keys(expiryMetrics);

      const promises = endpointKeys.map((key) =>
        fetch(
          `${baseUrl}/api/cpayscount/${company_code_from_local_storage}/${key}/-/-`
        )
          .then((res) => (res.ok ? res.json() : []))
          .then((data) => {
            if (Array.isArray(data) && data.length > 0) {
              const row = data[0];
              if (row.Cnt !== undefined) {
                updatedMetrics[key].count = row.Cnt;
              } else if (row.Column1 !== undefined) {
                updatedMetrics[key].count = row.Column1;
              } else {
                const firstKey = Object.keys(row)[0];
                updatedMetrics[key].count = firstKey ? row[firstKey] : 0;
              }
            } else {
              updatedMetrics[key].count = 0;
            }
          })
          .catch(() => {
            updatedMetrics[key].count = 0;
          })
      );

      await Promise.all(promises);
      setExpiryMetrics(updatedMetrics);
      setIsLoadingMetrics(false);
    };

    if (baseUrl && company_code_from_local_storage) {
      fetchExpiryData();
    }
  }, [baseUrl, company_code_from_local_storage]);

  // --- Click Handler to Fetch List Details ---
  const handleDocCardClick = async (key, cardInfo) => {
    setActiveDocModal(cardInfo);
    setIsLoadingDocDetails(true);
    setDocDetailsData([]);

    try {
      const response = await fetch(
        `${baseUrl}/api/CpaysCount/${company_code_from_local_storage}/${cardInfo.listEndpoint}/-/-`
      );
      if (!response.ok) throw new Error("Failed to load list details");
      const data = await response.json();
      setDocDetailsData(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching list data details:", error);
    } finally {
      setIsLoadingDocDetails(false);
    }
  };

  // --- Validate punch lat/long and open the location in Google Maps ---
  const getValidCoords = (punch) => {
    const lat = parseFloat(punch.latitude);
    const lng = parseFloat(punch.longitude);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
    if (lat === 0 && lng === 0) return null;
    return { lat, lng };
  };

   
  const handleViewLocation = (punch) => {
    const coords = getValidCoords(punch);
    if (!coords) return;
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${coords.lat},${coords.lng}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  // --- Handler to toggle individual employee time logs ---
  const toggleEmpPunchLogs = async (empId) => {
    if (expandedEmpId === empId) {
      setExpandedEmpId(null);
      setPunchData([]);
      return;
    }
    setExpandedEmpId(empId);
    setIsLoadingDetails(true);
    setPunchData([]);
    try {
      const response = await fetch(
        `${baseUrl}/api/cpayscount/${company_code_from_local_storage}/PUNCHLOGS/${empId}/-`
      );
      if (response.ok) {
        const data = await response.json();
        setPunchData(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error("Error fetching punch details:", err);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const cards = [
    {
      id: "present",
      title: "Present Employees",
      count: presentData?.length,
      bg: "bg-emerald-50 border-emerald-200 text-emerald-700",
      badge: "bg-emerald-500",
      data: presentData,
    },
    {
      id: "absent",
      title: "Absent Employees",
      count: absentData?.length,
      bg: "bg-rose-50 border-rose-200 text-rose-700",
      badge: "bg-rose-500",
      data: absentData,
    },
    {
      id: "leave",
      title: "On Leave",
      count: leaveData?.length,
      bg: "bg-amber-50 border-amber-200 text-amber-700",
      badge: "bg-amber-500",
      data: leaveData,
    },
  ];

  const activeGroup = cards.find((c) => c.id === activeModal);

  const handleCloseModal = () => {
    setActiveModal(null);
    setExpandedEmpId(null);
    setPunchData([]);
  };

  const fetchProfilePic = (ImagePath) => {
    const filePath = ImagePath.replace(/\\/g, "/");
    const fileName = filePath.split("/").pop(); // Extracts the last part (filename with extension)

    let imageViewingBaseUrl = apiPaths_from_local_storage[0]?.IMG_API_PATH;

    console.log("imageViewingBaseUrl ", imageViewingBaseUrl);

    let fullimageUrl = `${imageViewingBaseUrl}/BONDTIME_EMP_IMG/${fileName}`;

    console.log("full image url ", imageViewingBaseUrl);

    // Construct the full URL for the image
    return fullimageUrl;
  };

  const formatPunchTime = (rawTime) => {
    if (!rawTime) return "—";
    const date = new Date(rawTime);
    if (isNaN(date.getTime())) return rawTime; // not a parseable date, show as-is
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <div className="p-4 max-w-5xl mx-auto font-sans space-y-4">
      {/* Attendance Summary Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {cards.map((card) => (
          <button
            key={card.id}
            onClick={() => setActiveModal(card.id)}
            className={`p-4 rounded-xl border transition-all duration-200 transform hover:scale-[1.02] hover:shadow-md active:scale-95 text-left outline-none cursor-pointer ${card.bg}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold tracking-wider uppercase opacity-80">
                  {card.title}
                </p>
                <p className="text-3xl font-bold mt-1">{card.count}</p>
              </div>
              <div
                className={`w-3 h-3 rounded-full ${card.badge} animate-pulse`}
              />
            </div>

            <div className="mt-2 text-[10px] font-medium opacity-60 flex items-center gap-1">
              <span>Click to view</span>
              <span>→</span>
            </div>
          </button>
        ))}
      </div>

      {/* Interactive Expiry & Document Board */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-bold text-gray-700 tracking-wider uppercase">
            Expiry & Document Overview (Click to View)
          </h3>
          {isLoadingMetrics && (
            <span className="text-[10px] text-gray-400 animate-pulse">
              Syncing...
            </span>
          )}
        </div>

        {isLoadingMetrics ? (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[...Array(10)].map((_, i) => (
              <div
                key={i}
                className="h-16 rounded-lg bg-gray-50 border border-gray-100 animate-pulse"
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {Object.entries(expiryMetrics).map(([key, item]) => (
              <button
                key={key}
                onClick={() => handleDocCardClick(key, item)}
                className={`p-3 rounded-lg border flex flex-col justify-between transition-all active:scale-98 text-left group hover:shadow-xs outline-none cursor-pointer ${item.color}`}
              >
                <div className="flex items-center justify-between gap-1 w-full">
                  <span className="text-[10px] font-bold tracking-wide uppercase opacity-75 truncate group-hover:underline">
                    {item.title}
                  </span>
                  <div className="opacity-60 shrink-0">{item.icon}</div>
                </div>
                <span className="text-2xl font-black mt-1 tracking-tight">
                  {item.count}
                </span>

                <div
                  style={{
                    marginTop: 4,
                    fontSize: 10,
                    fontWeight: 500,
                    color: "#94a3b8",
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                    whiteSpace: "nowrap",
                  }}
                >
                  <span>Click to view</span>
                  <span style={{ fontSize: 10 }}>→</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Attendance Analysis Section */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
        <AttendanceAnalysis
          baseUrl={baseUrl}
          company_code_from_local_storage={company_code_from_local_storage}
        />
      </div>

      {/* NEW: Pending Leave Approvals — approve/reject straight from the dashboard */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
        <PendingLeaveApprovals
          baseUrl={baseUrl}
          companyCode={company_code_from_local_storage}
          loggedEmpId={loggedEmpId}
        />
      </div>

      {/* Mobile Responsive Leave Approvals Log Table (read-only history) */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
        <div className="mb-3">
          <h3 className="text-xs font-bold text-gray-700 tracking-wider uppercase">
            Leave Approvals History
          </h3>
        </div>
        <LeaveApprovalsTable
          baseUrl={baseUrl}
          companyCode={company_code_from_local_storage}
        />
      </div>

      {/* Document List Details Modal */}
      {activeDocModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 bg-black/40 backdrop-blur-xs transition-opacity">
          <div
            className="absolute inset-0"
            onClick={() => setActiveDocModal(null)}
          />
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[75vh] flex flex-col z-10 relative border border-gray-100">
            {/* Modal Header */}
            <div className="px-4 py-3 border-b flex items-center justify-between bg-white">
              <div className="flex items-center gap-2">
                <span className="text-gray-500">{activeDocModal.icon}</span>
                <h3 className="text-sm font-bold text-gray-900">
                  {activeDocModal.title} Detailed List
                </h3>
              </div>
              <button
                onClick={() => setActiveDocModal(null)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-1 overflow-y-auto bg-gray-50/50 flex-1 space-y-2">
              {isLoadingDocDetails ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400 gap-2 text-xs">
                  <div className="w-5 h-5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                  <span>Loading records from ERP database...</span>
                </div>
              ) : docDetailsData.length === 0 ? (
                <p className="text-center text-xs text-gray-400 py-10">
                  No records found under this category.
                </p>
              ) : (
                <div className="divide-y divide-gray-100 rounded-lg border border-gray-200/60 bg-white overflow-hidden shadow-xs">
                  {docDetailsData.map((row, idx) => {
                    const formatDate = (dateStr) => {
                      if (!dateStr || dateStr.startsWith("1900-01-01"))
                        return null;
                      return dateStr.split("T")[0];
                    };

                    let mainTitle = row.Name || "";
                    let subText = row.JobTitle || "";
                    let docNumber = "";
                    let expiryDate = null;
                    let daysExp = row.Days_of_Exp;

                    switch (activeDocModal.listEndpoint) {
                      case "PASSPORTEXPLIST":
                        docNumber = `Passport: ${row.PassportNo || ""}`;
                        expiryDate = formatDate(row.PassportExpiryDate);
                        break;
                      case "DRIVINGLICEXPLIST":
                        docNumber = `License: ${
                          row["DrivingLicense No"] || ""
                        }`;
                        expiryDate = formatDate(row["Driving LicenseExpiry"]);
                        break;
                      case "VISAEXPLIST":
                        docNumber = `Visa: ${row.VisaNo || ""}`;
                        expiryDate = formatDate(row.VisaExpiryDate);
                        break;
                      case "INSURANCEEXPLIST":
                        docNumber = `Ins Policy: ${row.InsuranceNo || ""}`;
                        expiryDate = formatDate(row.InsuranceExpiryDate);
                        break;
                      case "NATIONALIDEXPLIST":
                        docNumber = `Emirates ID: ${row.EmiratesID || ""}`;
                        expiryDate = formatDate(row.EmiratesIDExpiryDate);
                        break;
                      case "COMPANYDOCSEXPLIST":
                        mainTitle = row.Name || "Trade License";
                        subText = `Doc ID: ${row.docId}`;
                        expiryDate = formatDate(row.exp_date);
                        break;
                      case "PERSONIDEXPLIST":
                        docNumber = `Person ID: ${row.Person_ID || ""}`;
                        expiryDate = formatDate(row.Person_ID_Expiry);
                        break;
                      case "PARTTIMELIST":
                        docNumber = `Part-Time Ref: ${row.parttime || ""}`;
                        expiryDate = formatDate(row.parttimeexp);
                        break;
                      case "FLEETLIST":
                        mainTitle = row.Reg_No || "Unknown Vehicle";
                        subText =
                          [row.Name?.trim(), row.Type, row.Ins_type]
                            .filter(Boolean)
                            .join(" | ") || "Fleet Record";
                        expiryDate = formatDate(row.Expiry);
                        break;
                      case "FLEETINSURANCE":
                        mainTitle = row.Reg_No || "Unknown Vehicle";
                        subText = row.Make
                          ? `Make: ${row.Make}`
                          : "Fleet Insurance File";
                        expiryDate = formatDate(row.InsuranceExpiryDate);
                        break;
                      default:
                        mainTitle = row.Name || `Record #${idx + 1}`;
                    }

                    return (
                      <div
                        key={row.EmpId || row.Reg_No || row.docId || idx}
                        className="px-1 py-2 text-xs hover:bg-gray-50/80 transition-colors"
                      >
                        <div className="flex">
                          {row.imagepath ? (
                            <img
                              className="w-[40px] h-[40px] object-cover rounded-full shadow-md"
                              src={fetchProfilePic(row.imagepath)}
                              alt="Profile"
                            />
                          ) : (
                            <img
                              className="w-[50px] h-[50px] object-cover"
                              src={no_user_image}
                              alt="Default Profile"
                            />
                          )}
                          <div className="flex items-start justify-between gap-3 ms-2">
                            <div className="min-w-0 flex-1">
                              <p className="font-semibold text-gray-800 text-[13px] truncate">
                                {mainTitle}
                              </p>

                              {/* expiry ends  */}
                              <div className="text-right shrink-0">
                                {expiryDate ? (
                                  <div className="flex flex-col items-start gap-1">
                                    <span
                                      className={`text-[11px] font-bold px-2 py-0.5 rounded-md border ${
                                        daysExp < 0
                                          ? "bg-rose-50 border-rose-100 text-rose-600"
                                          : "bg-emerald-50 border-emerald-100 text-emerald-600"
                                      }`}
                                    >
                                      Exp: {expiryDate}
                                    </span>
                                    {daysExp !== undefined && (
                                      <span className="text-[10px] text-gray-400">
                                        {daysExp < 0
                                          ? `${Math.abs(daysExp)} days ago`
                                          : `${daysExp} days left`}
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-[11px] px-2 py-0.5 rounded bg-gray-100 text-gray-400 font-medium">
                                    No Expiry Date
                                  </span>
                                )}
                              </div>

                              {/* expiry ends  */}
                              <p className="text-gray-500 font-medium text-[11px] mt-0.5 truncate">
                                {subText}
                              </p>
                              {docNumber && (
                                <p className="mt-1 font-mono text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded w-max">
                                  {docNumber}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Standard Core Attendance Details Modal */}
      {activeGroup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 bg-black/40 backdrop-blur-xs transition-opacity">
          <div className="absolute inset-0" onClick={handleCloseModal} />
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden max-h-[70vh] flex flex-col z-10 relative border border-gray-100">
            {/* Header */}
            <div className="px-4 py-3 border-b flex items-center justify-between bg-white">
              <h3 className="text-sm font-bold text-gray-900">
                {activeGroup.title} ({activeGroup.count})
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* List Body */}
            <div className="p-1 overflow-y-auto bg-gray-50/50 flex-1 space-y-2">
              {activeGroup.data.length === 0 ? (
                <p className="text-center text-xs text-gray-400 py-10">
                  No employees in this list today.
                </p>
              ) : (
                <div className="divide-y divide-gray-100 rounded-lg border border-gray-200/60 bg-white overflow-hidden shadow-xs">
                  {activeGroup.data.map((emp, idx) => (
                    <div
                      key={emp.EmpId || idx}
                      className="p-3 text-xs hover:bg-gray-50/60 transition-colors"
                    >
                      <div className="flex">
                        {emp.imagepath ? (
                          <img
                            className="w-[50px] h-[50px] object-cover rounded-full shadow-md"
                            src={fetchProfilePic(emp.imagepath)}
                            alt="Profile"
                          />
                        ) : (
                          <img
                            className="w-[50px] h-[50px] object-cover"
                            src={no_user_image}
                            alt="Default Profile"
                          />
                        )}

                        <div className="flex items-center justify-between gap-3 ms-4">
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold text-gray-800 text-[13px] truncate">
                              {emp.Name || "Unknown Employee"}
                            </p>
                            <p className="text-gray-500 text-[11px] mt-0.5 truncate">
                              {emp.JobTitle || "Staff"}
                            </p>
                            <p className="mt-1 font-mono text-[10px] text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded w-max">
                              ID:---- {emp.EmpId}
                            </p>
                          </div>
                          {activeModal === "present" && (
                            <button
                              onClick={() => toggleEmpPunchLogs(emp.EmpId)}
                              className="text-[11px] font-medium text-indigo-600 hover:text-indigo-800 bg-indigo-50 px-2 py-1 rounded transition-colors shrink-0 outline-none cursor-pointer"
                            >
                              {expandedEmpId === emp.EmpId
                                ? "Hide Logs"
                                : "View Logs"}
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Expandable Live Punch logs */}
                      {expandedEmpId === emp.EmpId && (
                        <div className="mt-2 pt-2 border-t border-dashed border-gray-100 bg-gray-50/50 p-2 rounded-md">
                          {isLoadingDetails ? (
                            <div className="text-[10px] text-gray-400 animate-pulse text-center py-2">
                              Fetching times...
                            </div>
                          ) : punchData.length === 0 ? (
                            <div className="text-[10px] text-gray-400 text-center py-2">
                              No active time punches registered today.
                            </div>
                          ) : (
                            <div className="grid grid-cols-2 gap-1.5">
                              {punchData.map((punch, pIdx) => {
                                const rawTime = punch.punch_time || punch.Time;
                                const isIn = (punch.type || "").toUpperCase() === "IN";
                                const coords = getValidCoords(punch);
                                return (
                                  <div
                                    key={punch.id ?? pIdx}
                                    className="bg-white p-1 rounded border border-gray-100 text-[10px] text-gray-600 font-mono flex flex-col gap-1"
                                  >
                                    <div className="flex justify-between">
                                      <span
                                        className={`font-semibold ${
                                          isIn ? "text-emerald-600" : "text-rose-600"
                                        }`}
                                      >
                                        {punch.type || "Log"}:
                                      </span>
                                      <span>{formatPunchTime(rawTime)}</span>
                                    </div>
                                    {coords && (
                                      <button
                                        onClick={() => handleViewLocation(punch)}
                                        className="flex items-center justify-center gap-1 text-[9px] font-sans font-medium text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 rounded px-1 py-0.5 transition-colors cursor-pointer"
                                      >
                                        <svg
                                          className="w-2.5 h-2.5"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                          stroke="currentColor"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z"
                                          />
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                          />
                                        </svg>
                                        View Location
                                      </button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}